import Comscore from './index';

// CLEANUP: might be nice to abstract any assumption about player object here,
// should we decide to use a new video player.

// key is JW event name; value is Comscore event name (or function, see below)
const EVENT_MAP = {
  buffer: 'BufferStart',
  play: 'Play',
  pause: 'Pause',
  complete: 'End',
  seek: 'SeekStart',

  // seeked is special -- it can be either of two states after seeking (clicking
  // on timeline) -- so we need to dynamically determine the comscore event name
  // based on current player state
  seeked: currentState => {
    if (currentState === 'playing') {
      return 'Play';
    } else if (currentState === 'paused') {
      return 'Pause';
    }
    return null;
  }
};

/**
 * generic event to handle changes to the player state. makes calls to comscore
 * when applicable.
 *
 * @param {Object} e player event
 * @param {String} e.oldstate name of previous state
 * @param {String} e.type event type/name
 * @param {Object} player player object
 * @param {Function} player.getPosition method that returns player position as a Float
 * @param {Function} player.getState method that returns player state as a String
 * @returns {undefined}
 */
function playerStateChange(e, player) {
  const { oldstate, type } = e;

  // using Math.floor() because value returned by getPostion() is a float and we
  // don't need any more resolution beyond MS.
  const position = Math.floor(player.getPosition() * 1000);

  // we always want to know when buffering has stopped
  if (oldstate === 'buffering') {
    Comscore.notify('BufferStop', position);
  }

  // normal (non-seeked) events are a straight mapping
  if (type !== 'seeked') {
    Comscore.notify(EVENT_MAP[type], position);
    return;
  }

  // handle seeking special case: derive event name from current state, not type
  // of event
  const playerState = player.getState();
  const comscoreEventName = EVENT_MAP[type](playerState);

  if (comscoreEventName) {
    Comscore.notify(comscoreEventName, position);
  }
}

// event handlers for JWVideo component
export default {
  /**
   * before play:
   * - gets current comscore asset
   * - gets current player item
   * - if player is not same as comscore (i.e. playing a new video)
   *    - creates new playback session
   *    - sets current comscore asset with player item's vertical
   */
  onBeforePlay: (e, player, category) => {
    const comscoreAssetId = Comscore.getCurrentAssetId();
    const currentItem = player.getPlaylistItem();

    if (comscoreAssetId !== currentItem.mediaid) {
      Comscore.createPlaybackSession();
      Comscore.setCurrentAsset(currentItem, 'content', category);
    }
  },

  onBuffer: (e, player) => {
    playerStateChange(e, player);
  },

  // Add `onPlay`, `onResume` and `onAutoStart` to handle all JW `play` events.
  // NOTE: The react-jw-player `onPlay` handler does not map to JW's `play`
  // event. Instead `onPlay` is only called for the first play, and subsequent
  // plays will call the `onResume` handler. If the video autoplays, it will
  // call the `onAutoStart` handler instead of `onPlay`.
  // NOTE: `e.type` is 'play' in all of these handlers.
  onPlay: (e, player) => {
    playerStateChange(e, player);
  },

  onResume: (e, player) => {
    playerStateChange(e, player);
  },

  onAutoStart: (e, player) => {
    playerStateChange(e, player);
  },

  onPause: (e, player) => {
    playerStateChange(e, player);
  },

  onComplete: (e, player) => {
    playerStateChange(e, player);
  },

  onSeek: (e, player) => {
    playerStateChange(e, player);
  },

  onSeeked: (e, player) => {
    playerStateChange(e, player);
  },

  /**
   * on ad play:
   * - get current comscore asset
   * - if current asset is not same as ad id
   *   - set current comscore asset to ad with current vertical
   * -
   */
  onAdPlay: (ad, player, category) => {
    const comscoreAssetId = Comscore.getCurrentAssetId();

    // only set the ad asset on first play
    if (comscoreAssetId !== ad.id) {
      Comscore.setCurrentAsset(ad, 'ad', category);
    }

    Comscore.notify('Play');
  },

  /**
   * on ad pause:
   * - send pause event
   */
  onAdPause: () => {
    Comscore.notify('Pause');
  },

  /**
   * on ad complete:
   * - get next video item
   * - send End event
   * - set current comscore asset to new video with current vertical
   */
  onAdComplete: (e, player, category) => {
    const currentItem = player.getPlaylistItem();
    Comscore.notify('End');
    Comscore.setCurrentAsset(currentItem, 'content', category);
  },

  onAdError: () => {}
};
